import React, { useState } from "react";
import { Plus, Loader2, Rocket } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { useActiveProject } from "@/lib/ActiveProjectContext";
import { toast } from "sonner";

const FIDIC = ["Red Book", "Yellow Book", "Silver Book", "Green Book"];

const empty = {
  name: "",
  client: "",
  contract_type: "Yellow Book",
  value_usd_mn: 0,
  completion_target: "",
  description: "",
  budget_usd_mn: 0,
  spent_usd_mn: 0,
  progress_percent: 0,
  schedule_slip_days: 0,
  active_claims: 0,
  notes: "",
};

export default function NewProjectDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const { setActiveProjectId } = useActiveProject();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.client.trim()) {
      toast.error("Project name and client are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/projects", {
        ...form,
        value_usd_mn: Number(form.value_usd_mn) || 0,
        budget_usd_mn: Number(form.budget_usd_mn) || 0,
        spent_usd_mn: Number(form.spent_usd_mn) || 0,
        progress_percent: Number(form.progress_percent) || 0,
        schedule_slip_days: Number(form.schedule_slip_days) || 0,
        active_claims: Number(form.active_claims) || 0,
      });
      setActiveProjectId(data.id);
      toast.success(`${data.name} is now your active project`);
      setForm(empty);
      onOpenChange(false);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(
          e?.response?.data?.detail ||
            "Choose a pricing plan to add your first live project."
        );
      } else {
        toast.error(e?.response?.data?.detail || "Could not create project");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 border-slate-800 text-slate-100 max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto scroll-clean"
        data-testid="new-project-dialog"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-1 flex items-center gap-2">
            <Rocket className="h-3 w-3" />
            Add Live Project
          </div>
          <DialogTitle className="font-display text-2xl">
            Create a project workspace
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            These details will auto-prefill every AI module — Contract, Project,
            Finance, Automation and Training.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <Field label="Project name *" testid="np-name">
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. 300 MW Wind Farm — Gujarat"
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-name"
            />
          </Field>
          <Field label="Client / Employer *">
            <Input
              value={form.client}
              onChange={(e) => set("client", e.target.value)}
              placeholder="e.g. NTPC Limited"
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-client"
            />
          </Field>
          <Field label="FIDIC Contract">
            <Select
              value={form.contract_type}
              onValueChange={(v) => set("contract_type", v)}
            >
              <SelectTrigger
                className="bg-slate-900 border-slate-800 rounded-sm"
                data-testid="np-contract-type"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                {FIDIC.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Contract value (USD mn)">
            <Input
              type="number"
              value={form.value_usd_mn}
              onChange={(e) => set("value_usd_mn", e.target.value)}
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-value"
            />
          </Field>
          <Field label="Target completion">
            <Input
              type="date"
              value={form.completion_target}
              onChange={(e) => set("completion_target", e.target.value)}
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-target"
            />
          </Field>
          <Field label="Budget (USD mn)">
            <Input
              type="number"
              value={form.budget_usd_mn}
              onChange={(e) => set("budget_usd_mn", e.target.value)}
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-budget"
            />
          </Field>
          <Field label="Spent to-date (USD mn)">
            <Input
              type="number"
              value={form.spent_usd_mn}
              onChange={(e) => set("spent_usd_mn", e.target.value)}
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-spent"
            />
          </Field>
          <Field label="Progress %">
            <Input
              type="number"
              value={form.progress_percent}
              onChange={(e) => set("progress_percent", e.target.value)}
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-progress"
            />
          </Field>
          <Field label="Schedule slip (days)">
            <Input
              type="number"
              value={form.schedule_slip_days}
              onChange={(e) => set("schedule_slip_days", e.target.value)}
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-slip"
            />
          </Field>
          <Field label="Active claims">
            <Input
              type="number"
              value={form.active_claims}
              onChange={(e) => set("active_claims", e.target.value)}
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-claims"
            />
          </Field>
        </div>

        <div className="pt-2">
          <Field label="Scope description">
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="EPC scope, key equipment, locations, critical dependencies…"
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-description"
            />
          </Field>
        </div>
        <div>
          <Field label="Contextual notes for the AI">
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Delay events, cost pressures, ongoing disputes, weather impacts…"
              className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
              data-testid="np-notes"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm"
            data-testid="np-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="np-save"
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm"
          >
            {saving ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Creating…</>
            ) : (
              <><Plus className="h-3.5 w-3.5 mr-1.5" /> Create & Activate</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-400">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
